import { ChannelFilters, ChannelOptions, ChannelSort, StreamChat } from 'stream-chat';
import {
  ChannelList,
  Channel,
  Window,
  ChannelHeader,
  MessageInput,
  Thread,
  VirtualizedMessageList,
  Chat,
  SearchBar,
  ChannelSearchFunctionParams,
  TypingIndicator,
  Streami18n,
} from 'stream-chat-react';
import { arrowBackOutline } from 'ionicons/icons';
import { IonIcon, isPlatform } from '@ionic/react';
import { useHistory } from 'react-router';
import { PrivateRoutes } from 'src/routes/navigation';
import React, { SetStateAction, useCallback } from 'react';
import { EmojiPicker } from 'stream-chat-react/emojis';

import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';

import 'dayjs/locale/es';
import 'dayjs/locale/pl';
import 'dayjs/locale/uk';
import dayjs from 'dayjs';

type StreamChatChannelListWidgetProps = {
  userId: string;
  chatClient: StreamChat;
};

const StreamChatChannelListWidget: React.FC<StreamChatChannelListWidgetProps> = ({ userId, chatClient }) => {
  const {
    i18n: { language },
  } = useTranslation('getstream-chat');

  const history = useHistory();

  const filters: ChannelFilters = {
    members: { $in: [userId] },
    type: { $in: ['messaging', 'system'] },
  };
  const options: ChannelOptions = { presence: true, state: true };
  const sort: ChannelSort = { last_message_at: -1 };

  const dayjsLocale = dayjs.Ls?.[language];
  const streamI18n = new Streami18n({
    translationsForLanguage: i18n.getResourceBundle(language, 'getstream-chat'),
    dayjsLocaleConfigForLanguage: {
      months: dayjsLocale.months,
      monthsShort: dayjsLocale.monthsShort,
      calendar: {
        lastDay: `D MMMM, ${dayjsLocale.formats.LT}`,
        lastWeek: `D MMMM, ${dayjsLocale.formats.LT}`,
        nextDay: `D MMMM, ${dayjsLocale.formats.LT}`,
        nextWeek: `D MMMM, ${dayjsLocale.formats.LT}`,
        sameDay: `${dayjsLocale.formats.LT}`,
        sameElse: `D MMMM, ${dayjsLocale.formats.LT}`,
      },
    },
  });

  const customSearchFunction = useCallback(
    async (props: ChannelSearchFunctionParams, event: { target: { value: SetStateAction<string> } }) => {
      if (!chatClient) return;

      const { setResults, setSearching, setQuery } = props;
      const value = event.target.value;
      setSearching(true);
      setQuery(value);

      const filtersLocal: any = {
        members: { $in: [userId] },
        type: { $in: ['messaging', 'system'] },
        member_count: 2,
      };

      const channels = await chatClient.queryChannels(filtersLocal);

      const filteredChannels = channels.filter((channel) => {
        const otherMember = Object.values(channel.state.members).find((member) => member.user?.id !== userId);
        return otherMember?.user?.name?.toLowerCase().includes(value?.toString().toLowerCase());
      });

      setResults(filteredChannels);
      setSearching(false);
    },
    [chatClient]
  );

  return (
    <Chat client={chatClient} i18nInstance={streamI18n} theme="str-chat__theme-light">
      <div className="str-chat-custom_root">
        <ChannelList
          setActiveChannelOnMount={!isPlatform('mobile')}
          sort={sort}
          filters={filters}
          options={options}
          showChannelSearch={true}
          customActiveChannel={(history.location.state as { channelId?: string })?.channelId ?? ''}
          additionalChannelSearchProps={{
            searchFunction: customSearchFunction,
            searchForChannels: true,
            ExitSearchIcon: () => null,
            SearchBar: (props) => (
              <div className="flex items-center w-full pl-2.5">
                <IonIcon
                  icon={arrowBackOutline}
                  className="cursor-pointer text-[#000] text-2xl shrink-0"
                  onClick={() => {
                    ['PUSH', 'POP'].includes(history.action)
                      ? history.back()
                      : history.push(PrivateRoutes.PSYCHOLOGIST_LIST);
                  }}
                />
                <div className="w-full">
                  <SearchBar {...props} />
                </div>
              </div>
            ),
          }}
        />
        <div className="str-chat__main-panel">
          <Channel
            EmojiPicker={() => <EmojiPicker pickerProps={{ theme: 'light' }} />}
            TypingIndicator={TypingIndicator}
          >
            <Window>
              <ChannelHeader
                MenuIcon={() => (
                  <IonIcon className="cursor-pointer text-[#000] text-2xl shrink-0" icon={arrowBackOutline} />
                )}
              />
              <VirtualizedMessageList
                additionalVirtuosoProps={{ defaultItemHeight: 20, alignToBottom: true }}
                closeReactionSelectorOnClick={true}
                messageActions={['edit', 'delete', 'flag', 'quote', 'react', 'markUnread']}
              />
              <MessageInput audioRecordingEnabled={false} grow={true} />
            </Window>
            <Thread virtualized={true} additionalVirtualizedMessageListProps={{ defaultItemHeight: 20 }} />
          </Channel>
        </div>
      </div>
    </Chat>
  );
};

export default StreamChatChannelListWidget;
